import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Building2, Search, Sparkles, Loader2, AlertTriangle, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormDrawer } from '@/components/ui/form-drawer';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';

interface Account {
  id: string;
  name: string;
  code?: string;
  accountNumber?: string;
  externalId?: string;
  status: string;
  industry?: string;
  website?: string;
  creditHold?: boolean;
  notes?: string;
}

interface Location {
  id: string;
  name: string;
  code?: string;
  isPrimary?: boolean;
  account: Account;
}

interface AccountFormData {
  name: string;
  code: string;
  accountNumber: string;
  externalId: string;
  locationName: string;
  industry: string;
  phone: string;
  website: string;
  status: string;
  notes: string;
  parentAccountId?: string;
}

interface NameMatch {
  id: string;
  name: string;
  classification: string;
  locations: { id: string; name: string; isPrimary: boolean | null }[];
}

interface Party {
  id: string;
  legalName: string | null;
  dbaName: string | null;
  taxId: string | null;
}

const industries = [
  'Food Service',
  'Retail',
  'Healthcare',
  'Education',
  'Hospitality',
  'Manufacturing',
  'Wholesale',
  'Other',
];

export function AccountsPage() {
  const { sessionId } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  
  const [newAccountDrawerOpen, setNewAccountDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    code: '',
    accountNumber: '',
    externalId: '',
    locationName: 'Main',
    industry: '',
    phone: '',
    website: '',
    status: 'active',
    notes: '',
    parentAccountId: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [nameMatches, setNameMatches] = useState<NameMatch[]>([]);
  const [selectedExistingAccount, setSelectedExistingAccount] = useState<NameMatch | null>(null);
  const [partySearch, setPartySearch] = useState('');
  const [partyResults, setPartyResults] = useState<Party[]>([]);
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [showPartyLink, setShowPartyLink] = useState(false);
  const [parentAccountSearch, setParentAccountSearch] = useState('');
  const [selectedParentAccount, setSelectedParentAccount] = useState<Account | null>(null);
  const [isSuggestingCode, setIsSuggestingCode] = useState(false);

  // Fetch locations (accounts with their primary location)
  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations', sessionId],
    queryFn: async () => {
      const result = await api.get<{ accounts: Account[] }>('/accounts', sessionId);
      // Transform accounts to location format for display
      return result.accounts.map(account => ({
        id: account.id,
        name: 'Main',
        code: account.code,
        isPrimary: true,
        account: account,
      })) as Location[];
    },
    enabled: !!sessionId,
  });

  // Fetch parent accounts for selection
  const { data: parentAccountsData } = useQuery({
    queryKey: ['parent-accounts', sessionId],
    queryFn: async () => {
      const result = await api.get<{ accounts: Account[] }>('/accounts', sessionId);
      return result.accounts;
    },
    enabled: !!sessionId && newAccountDrawerOpen,
  });

  const parentAccounts = parentAccountsData || [];
  const filteredParentAccounts = parentAccountSearch.length >= 2
    ? parentAccounts.filter(a => a.name.toLowerCase().includes(parentAccountSearch.toLowerCase()))
    : parentAccounts;

  // Check for duplicate names
  const checkName = useCallback(async (name: string) => {
    if (!name || name.length < 2) {
      setNameMatches([]);
      setSelectedExistingAccount(null);
      return;
    }
    try {
      const response = await fetch(`/api/accounts/check-name?name=${encodeURIComponent(name)}`, {
        headers: sessionId ? { 'X-Session-ID': sessionId } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setNameMatches(data.matches || []);
        setSelectedExistingAccount(null);
      }
    } catch {
      setNameMatches([]);
    }
  }, [sessionId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      checkName(formData.name);
    }, 400);
    return () => clearTimeout(timeout);
  }, [formData.name, checkName]);

  // Search parties
  useEffect(() => {
    if (!partySearch || partySearch.length < 2) {
      setPartyResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const response = await fetch(`/api/parties/search?q=${encodeURIComponent(partySearch)}`, {
          headers: sessionId ? { 'X-Session-ID': sessionId } : {},
        });
        if (response.ok) {
          const data = await response.json();
          setPartyResults(data || []);
        }
      } catch {
        setPartyResults([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [partySearch, sessionId]);

  // Suggest code with AI
  const handleSuggestCode = async () => {
    if (!formData.name || isSuggestingCode) return;
    
    setIsSuggestingCode(true);
    try {
      const response = await fetch('/api/codes/suggest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
        },
        body: JSON.stringify({
          entityType: 'account',
          name: formData.name,
          useAI: true,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData({ ...formData, code: data.code });
      }
    } catch (error) {
      console.error('Failed to suggest code:', error);
    } finally {
      setIsSuggestingCode(false);
    }
  };

  // Filter locations
  const filteredLocations = (locations || []).filter(loc => {
    if (searchValue) {
      const q = searchValue.toLowerCase();
      const matches =
        loc.account.name.toLowerCase().includes(q) ||
        loc.name.toLowerCase().includes(q) ||
        loc.code?.toLowerCase().includes(q) ||
        loc.account.accountNumber?.toLowerCase().includes(q) ||
        loc.account.externalId?.toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (statusFilter !== 'all' && loc.account.status !== statusFilter) return false;
    return true;
  });

  // Create account mutation
  const createMutation = useMutation({
    mutationFn: async (data: AccountFormData) => {
      const { locationName, ...accountData } = data;
      return api.post('/accounts', {
        ...accountData,
        locationName,
        existingAccountId: selectedExistingAccount?.id,
        ...(selectedParty ? { partyId: selectedParty.id } : {}),
        ...(selectedParentAccount ? { parentAccountId: selectedParentAccount.id } : {}),
      }, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setNewAccountDrawerOpen(false);
      resetForm();
      setNameMatches([]);
      setSelectedExistingAccount(null);
      setSelectedParty(null);
      setPartySearch('');
      setShowPartyLink(false);
      setSelectedParentAccount(null);
      setParentAccountSearch('');
    },
    onError: (error: Error) => {
      const msg = error.message || 'Failed to create account';
      if (/external id/i.test(msg)) {
        setFormErrors({ externalId: msg });
      } else if (/reference number/i.test(msg)) {
        setFormErrors({ accountNumber: msg });
      } else if (/code/i.test(msg)) {
        setFormErrors({ code: msg });
      } else {
        setFormErrors({ general: msg });
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      accountNumber: '',
      externalId: '',
      locationName: 'Main',
      industry: '',
      phone: '',
      website: '',
      status: 'active',
      notes: '',
      parentAccountId: '',
    });
    setFormErrors({});
    setNameMatches([]);
    setSelectedExistingAccount(null);
    setSelectedParty(null);
    setPartySearch('');
    setShowPartyLink(false);
    setSelectedParentAccount(null);
    setParentAccountSearch('');
  };

  const handleDrawerClose = (open: boolean) => {
    setNewAccountDrawerOpen(open);
    if (!open) {
      resetForm();
      setNameMatches([]);
      setSelectedExistingAccount(null);
      setSelectedParty(null);
      setPartySearch('');
      setShowPartyLink(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Account name is required';
    }
    
    if (!formData.locationName.trim()) {
      errors.locationName = 'Location name is required';
    }
    
    if (formData.code && formData.code.length > 16) {
      errors.code = 'Code must be 16 characters or less';
    }
    
    if (formData.code && !/^[A-Z0-9-]*$/.test(formData.code)) {
      errors.code = 'Use uppercase letters, digits, or hyphens';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      createMutation.mutate(formData);
    }
  };

  const handleRowClick = (accountId: string) => {
    setLocation(`/accounts/${accountId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your customer accounts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setNewAccountDrawerOpen(true)}
            data-testid="button-new-account"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Account
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search accounts, refs, or external IDs..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              Loading accounts...
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No accounts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Industry
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {filteredLocations.map((loc) => (
                    <tr
                      key={loc.id}
                      onClick={() => handleRowClick(loc.account.id)}
                      className="hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {loc.account.name}
                          </span>
                          {loc.account.creditHold && (
                            <Badge variant="destructive" className="text-xs">
                              Hold
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {loc.account.code || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {loc.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          loc.account.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : loc.account.status === 'on_hold'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {loc.account.status === 'on_hold' ? 'On Hold' : loc.account.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground capitalize">
                        {loc.account.industry || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Account Drawer */}
      <FormDrawer
        open={newAccountDrawerOpen}
        onOpenChange={handleDrawerClose}
        title="New Account"
        description="Create a new customer account"
        formId="create-account"
        isPending={createMutation.isPending}
        submitLabel={selectedExistingAccount ? 'Add Location' : 'Create Account'}
        size="md"
        data-testid="drawer-new-account"
      >
        <form id="create-account" onSubmit={handleSubmit} className="space-y-6">
          {/* Account Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Account Name *</Label>
            <Input
              id="name"
              variant="flat"
              placeholder="Enter account name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formErrors.name ? 'border-destructive' : ''}
              data-testid="input-account-name"
            />
            {formErrors.name && (
              <p className="text-sm text-destructive">{formErrors.name}</p>
            )}
          </div>

          {/* Code with AI Suggest */}
          <div className="space-y-2">
            <Label htmlFor="code">Code *</Label>
            <div className="flex gap-1">
              <Input
                id="code"
                variant="flat"
                placeholder="ACM"
                maxLength={16}
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className={formErrors.code ? 'border-destructive' : ''}
                data-testid="input-account-code"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Suggest code"
                disabled={!formData.name || isSuggestingCode}
                onClick={handleSuggestCode}
                data-testid="button-suggest-code"
              >
                {isSuggestingCode ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
            {formErrors.code && (
              <p className="text-sm text-destructive">{formErrors.code}</p>
            )}
          </div>

          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="accountNumber">Reference Number</Label>
            <Input
              id="accountNumber"
              variant="flat"
              placeholder="ERP / legacy ID (optional)"
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              className={formErrors.accountNumber ? 'border-destructive' : ''}
              data-testid="input-account-number"
            />
            {formErrors.accountNumber && (
              <p className="text-sm text-destructive">{formErrors.accountNumber}</p>
            )}
          </div>

          {/* Integration (advanced) - Collapsible */}
          <details className="rounded-md border border-border bg-muted/40 px-3 py-2 group">
            <summary className="text-sm font-medium cursor-pointer select-none flex items-center justify-between">
              <span>Integration (advanced)</span>
              <span className="text-xs text-muted-foreground group-open:hidden">Show</span>
            </summary>
            <div className="pt-3">
              <div className="space-y-2">
                <Label htmlFor="externalId">External ID</Label>
                <Input
                  id="externalId"
                  variant="flat"
                  placeholder="e.g. qbo:12345"
                  value={formData.externalId}
                  onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                  className={formErrors.externalId ? 'border-destructive' : ''}
                  data-testid="input-external-id"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Usually set automatically by a connected system. Leave blank if you're not sure.
                </p>
                {formErrors.externalId && (
                  <p className="text-sm text-destructive">{formErrors.externalId}</p>
                )}
              </div>
            </div>
          </details>

            {/* Name Match Warning */}
            {nameMatches.length > 0 && !selectedExistingAccount && (
              <Card className="border-amber-500/50 bg-amber-500/5">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    Name already exists
                  </div>
                  <p className="text-sm text-muted-foreground">
                    A record with this name already exists. You can add a new location to it, or change the name to create a separate entry.
                  </p>
                  {nameMatches.map((match) => (
                    <div key={match.id} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{match.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {match.classification === 'prospect' ? 'Lead' : 'Account'}
                        </Badge>
                      </div>
                      {match.locations.length > 0 && (
                        <div className="space-y-1 ml-2">
                          <div className="text-xs text-muted-foreground">Existing locations:</div>
                          {match.locations.map((loc) => (
                            <div key={loc.id} className="flex items-center gap-2 text-sm">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                              <span>{loc.name}</span>
                              {loc.isPrimary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedExistingAccount(match)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add new location to this {match.classification === 'prospect' ? 'lead' : 'account'}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Selected Existing Account */}
            {selectedExistingAccount && (
              <Card className="border-blue-500/50 bg-blue-500/5">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-blue-600">
                      Adding location to: {selectedExistingAccount.name}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedExistingAccount(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                  {selectedExistingAccount.locations.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">Current locations:</div>
                      {selectedExistingAccount.locations.map((loc) => (
                        <div key={loc.id} className="flex items-center gap-2 text-sm">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span>{loc.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Location Name */}
            <div className="space-y-2">
              <Label htmlFor="locationName">Location Name *</Label>
              <Input
                id="locationName"
                variant="flat"
                placeholder="e.g. Main, HQ, Warehouse"
                value={formData.locationName}
                onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                className={formErrors.locationName ? 'border-destructive' : ''}
                data-testid="input-location-name"
              />
              {formErrors.locationName && (
                <p className="text-sm text-destructive">{formErrors.locationName}</p>
              )}
            </div>

            {/* Only show these fields if not adding to existing account */}
            {!selectedExistingAccount && (
              <>
                {/* Link to existing party */}
                <div className="space-y-2">
                  {!showPartyLink ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground"
                      onClick={() => setShowPartyLink(true)}
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      Link to existing party (optional)
                    </Button>
                  ) : selectedParty ? (
                    <Card className="border-green-500/50 bg-green-500/5">
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-700 dark:text-green-400">
                            Linked to: {selectedParty.dbaName || selectedParty.legalName}
                          </p>
                          {selectedParty.taxId && (
                            <p className="text-xs text-muted-foreground">Tax ID: {selectedParty.taxId}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedParty(null); setPartySearch(''); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                          <Input
                            variant="flat"
                            placeholder="Search parties by name, tax ID..."
                            value={partySearch}
                            onChange={(e) => setPartySearch(e.target.value)}
                            className="pl-8 h-9 text-sm"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setShowPartyLink(false); setPartySearch(''); setPartyResults([]); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {partyResults.length > 0 && (
                        <div className="border rounded-md max-h-60 overflow-y-auto">
                          {partyResults.map((party) => (
                            <button
                              key={party.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover-elevate active-elevate-2 border-b last:border-b-0"
                              onClick={() => { setSelectedParty(party); setPartyResults([]); setPartySearch(''); }}
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{party.dbaName || party.legalName || 'Unnamed Party'}</span>
                                {party.taxId && <span className="text-xs text-muted-foreground">Tax: {party.taxId}</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {partySearch.length >= 2 && partyResults.length === 0 && (
                        <p className="text-xs text-muted-foreground">No parties found</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Parent Account */}
                <div className="space-y-2">
                  <Label className="text-sm">Parent Account (optional)</Label>
                  {selectedParentAccount ? (
                    <Card className="border-indigo-500/50 bg-indigo-500/5">
                      <CardContent className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
                            Child of: {selectedParentAccount.name}
                          </p>
                          {selectedParentAccount.accountNumber && (
                            <p className="text-xs text-muted-foreground">{selectedParentAccount.accountNumber}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedParentAccount(null); setParentAccountSearch(''); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          variant="flat"
                          placeholder="Search for parent account..."
                          value={parentAccountSearch}
                          onChange={(e) => setParentAccountSearch(e.target.value)}
                          className="pl-8 h-9 text-sm"
                        />
                      </div>
                      {parentAccountSearch.length >= 2 && filteredParentAccounts.length > 0 && (
                        <div className="border rounded-md max-h-40 overflow-y-auto">
                          {filteredParentAccounts.slice(0, 10).map((acct) => (
                            <button
                              key={acct.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover-elevate active-elevate-2 border-b last:border-b-0"
                              onClick={() => { setSelectedParentAccount(acct); setParentAccountSearch(''); }}
                            >
                              <span className="font-medium">{acct.name}</span>
                              {acct.accountNumber && <span className="ml-2 text-xs text-muted-foreground">{acct.accountNumber}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {parentAccountSearch.length >= 2 && filteredParentAccounts.length === 0 && (
                        <p className="text-xs text-muted-foreground">No accounts found</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Industry */}
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData({ ...formData, industry: value })}
                  >
                    <SelectTrigger id="industry" variant="flat" data-testid="select-industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry} value={industry.toLowerCase()}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Website */}
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    variant="flat"
                    placeholder="https://example.com"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    data-testid="input-website"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    variant="flat"
                    placeholder="Add any additional notes about this account..."
                    className="min-h-[100px]"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="textarea-notes"
                  />
                </div>
              </>
            )}

          {/* General Error */}
          {formErrors.general && (
            <div className="p-3 rounded-md border border-destructive/50 bg-destructive/10">
              <p className="text-sm text-destructive">{formErrors.general}</p>
            </div>
          )}
        </form>
      </FormDrawer>
    </div>
  );
}